/** The chips under a decision's name (DESIGN §4.1): "Standing policy: A" (neutral; a caption under the control on the sheet), "Maintained" (green) once kept as it is. One place, on the walk card and on an open sheet row. */
import type { ReactNode } from "react";
import type { DecisionCatalogEntry } from "@bsim/contracts";
import type { Decisions } from "@bsim/domain";
import { changeTag } from "./decisionValues";
import { policyText } from "./deskSheet";
import { tradesSwaps } from "./because";
import { readDecision } from "./decisionValues";
import { swapsHeldPolicy, type Swap } from "../financials/swaps";
import { GxPill } from "../theme/GxPill";
import type { ReviewState } from "./reviewState";

export function DecisionChips({ entry, decisions, seed, state, held, who, heldSwaps, caption = false }: {
  /** The swaps the bank holds at round start: a rate position whose standing policy sets no target says it holds them, never "No hedge" beside a swap. */
  readonly heldSwaps?: readonly Swap[] | undefined;
  /** An open sheet row: the standing policy is a caption under the control. What changed is said by the "This year" pill, never by a chip here. */
  readonly caption?: boolean;
  readonly entry: DecisionCatalogEntry; readonly decisions: Decisions; readonly seed: Partial<Decisions>; readonly state: ReviewState;
  /** The standing policy sets it this round: one neutral chip says so. */
  readonly held: boolean;
  /** Who moved it, or that the save is still in doubt; null when there is nothing to say (a solo run stamps no name). */
  readonly who: ReactNode;
}) {
  const tag = changeTag(entry, decisions, seed);
  const policy = <><i className="gx-scale-mark gx-scale-mark--legend" aria-hidden="true" />Standing policy: {(tradesSwaps(entry) ? swapsHeldPolicy(heldSwaps, readDecision(seed as Decisions, entry.decisionId, "target")) : null) ?? policyText(entry, seed)}</>;
  return (
    <div className={`gx-decide-chips${caption ? " gx-decide-chips--cap" : ""}`} data-testid="decide.chips">
      {held ? <GxPill tone="neutral" testId="walk.chip">Set by your standing policy</GxPill> : <>
        {caption ? <span className="gx-small gx-muted" data-testid="walk.policy">{policy}</span> : <GxPill tone="neutral" testId="walk.policy">{policy}</GxPill>}
        {state === "maintained" ? <GxPill tone="good" testId="walk.maintained">Maintained</GxPill> : null}
      </>}
      {who === null || tag === null ? null : <span className="gx-decide-who gx-small" data-testid="walk.status">{who}</span>}
    </div>
  );
}
